import { memo, useMemo } from 'react';
import { motion, useCycle, useReducedMotion } from 'framer-motion';
import styles from './InfoCorner.module.css';

const Path = (props: any) => (
  <motion.path
    fill="transparent"
    strokeWidth="3"
    strokeLinecap="round"
    {...props}
  />
);
const MemoizedPath = memo(Path);

const menuVariant1 = {
  closed: { d: 'M 2 2.5 L 20 2.5' },
  open: { d: 'M 3 16.5 L 17 2.5' },
};
const menuVariant2 = {
  closed: { opacity: 1 },
  open: { opacity: 0 },
};
const transition2 = { duration: 0.1 };
const menuVariant3 = {
  closed: { d: 'M 2 16.346 L 20 16.346' },
  open: { d: 'M 3 2.5 L 17 16.346' },
};
const whileHover = { scale: 1.1 };

const MenuToggle = ({ toggle }: { toggle: Function }) => {
  return (
    <motion.button
      whileHover={whileHover}
      className={styles.toggleButton}
      // @ts-ignore-next-line
      onClick={toggle}
    >
      <svg viewBox="0 0 23 23">
        <MemoizedPath variants={menuVariant1} />
        <MemoizedPath
          d="M 2 9.423 L 20 9.423"
          variants={menuVariant2}
          transition={transition2}
        />
        <MemoizedPath variants={menuVariant3} />
      </svg>
    </motion.button>
  );
};
const MemoizedMenuToggle = memo(MenuToggle);

const variants = {
  open: (i: number) => ({
    pathLength: 1,
    transition: {
      duration: 0.4,
      delay: i * 0.4,
    },
  }),
  closed: (i: number) => ({
    pathLength: 0,
    transition: {
      duration: 0.3,
      delay: 0.8 - i * 0.3, // 0.8 is the total animation time (with 3 icons it would be 1.2)
    },
  }),
};
const variantsNoMotion = {
  open: { pathLength: 1, transition: { duration: 0 } },
  closed: { pathLength: 0, transition: { duration: 0 } },
};

function InfoCorner() {
  const shouldReduceMotion = useReducedMotion();
  const [isOpen, toggle] = useCycle(false, true);

  const whileHover = useMemo(
    () => (isOpen ? { scale: 0.9 } : { scale: 1 }),
    [isOpen]
  );

  return (
    <div className={styles.container}>
      <div className={styles.items}>
        <motion.nav
          className={styles.nav}
          initial={false}
          animate={isOpen ? 'open' : 'closed'}
        >
          <MemoizedMenuToggle toggle={toggle} />

          {/* twitter icon */}
          <a
            className={
              isOpen
                ? `${styles.link} ${styles.twitterIcon}`
                : `${styles.linkHidden} ${styles.twitterIcon}`
            }
            href="https://twitter.com/albertodeago88"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 256 209">
              <motion.path
                d="M256,25.4500259 C246.580841,29.6272672 236.458451,32.4504868 225.834156,33.7202333 C236.678503,27.2198053 245.00583,16.9269929 248.927437,4.66307685 C238.779765,10.6812633 227.539325,15.0523376 215.57599,17.408298 C205.994835,7.2006971 192.34506,0.822 177.239197,0.822 C148.232605,0.822 124.716076,24.3375931 124.716076,53.3423116 C124.716076,57.4586875 125.181462,61.4673784 126.076652,65.3112644 C82.4258385,63.1210453 43.7257252,42.211429 17.821398,10.4359288 C13.3005011,18.1929938 10.710443,27.2151234 10.710443,36.8402889 C10.710443,55.061526 19.9835254,71.1374907 34.0762135,80.5557137 C25.4660961,80.2832239 17.3681846,77.9207088 10.2862577,73.9869292 C10.2825122,74.2060448 10.2825122,74.4260967 10.2825122,74.647085 C10.2825122,100.094453 28.3867003,121.322443 52.413563,126.14673 C48.0059695,127.347184 43.3661509,127.988612 38.5755734,127.988612 C35.1914554,127.988612 31.9009766,127.659938 28.694773,127.046602 C35.3777973,147.913145 54.7742053,163.097665 77.7569918,163.52185 C59.7820257,177.607983 37.1354036,186.004604 12.5289147,186.004604 C8.28987161,186.004604 4.10888474,185.75646 0,185.271409 C23.2431033,200.173139 50.8507261,208.867532 80.5109185,208.867532 C177.116529,208.867532 229.943977,128.836982 229.943977,59.4326002 C229.943977,57.1552968 229.893412,54.8901664 229.792282,52.6381454 C240.053257,45.2331635 248.958338,35.9825545 256,25.4500259"
                fill="transparent"
                strokeWidth="10"
                whileHover={whileHover}
                variants={shouldReduceMotion ? variantsNoMotion : variants}
                custom={1}
              />
            </svg>
          </a>

          {/* github icon */}
          <a
            className={`${isOpen ? styles.link : styles.linkHidden} ${
              styles.githubIcon
            }`}
            href="https://github.com/albertodeago/cloc-web"
            rel="noreferrer"
            target="_blank"
          >
            <svg viewBox="0 0 256 250">
              <motion.path
                d="M128.00106,0 C57.3172926,0 0,57.3066942 0,128.00106 C0,184.555281 36.6761997,232.535542 87.534937,249.460899 C93.9320223,250.645779 96.280588,246.684165 96.280588,243.303333 C96.280588,240.251045 96.1618878,230.167899 96.106777,219.472176 C60.4967585,227.215235 52.9826207,204.369712 52.9826207,204.369712 C47.1599584,189.574598 38.770408,185.640538 38.770408,185.640538 C27.1568785,177.696113 39.6458206,177.859325 39.6458206,177.859325 C52.4993419,178.762293 59.267365,191.04987 59.267365,191.04987 C70.6837675,210.618423 89.2115753,204.961093 96.5158685,201.690482 C97.6647155,193.417512 100.981959,187.77078 104.642583,184.574357 C76.211799,181.33766 46.324819,170.362144 46.324819,121.315702 C46.324819,107.340889 51.3250588,95.9223682 59.5132437,86.9583937 C58.1842268,83.7344152 53.8029229,70.715562 60.7532354,53.0843636 C60.7532354,53.0843636 71.5019501,49.6441813 95.9626412,66.2049595 C106.172967,63.368876 117.123047,61.9465949 128.00106,61.8978432 C138.879073,61.9465949 149.837632,63.368876 160.067033,66.2049595 C184.49805,49.6441813 195.231926,53.0843636 195.231926,53.0843636 C202.199197,70.715562 197.815773,83.7344152 196.486756,86.9583937 C204.694018,95.9223682 209.660343,107.340889 209.660343,121.315702 C209.660343,170.478725 179.716133,181.303747 151.213281,184.472614 C155.80443,188.444828 159.895342,196.234518 159.895342,208.176593 C159.895342,225.303317 159.746968,239.087361 159.746968,243.303333 C159.746968,246.709601 162.05102,250.70089 168.53925,249.443941 C219.370432,232.499507 256,184.536204 256,128.00106 C256,57.3066942 198.691187,0 128.00106,0 Z M47.9405593,182.340212 C47.6586465,182.976105 46.6581745,183.166873 45.7467277,182.730227 C44.8183235,182.312656 44.2968914,181.445722 44.5978808,180.80771 C44.8734344,180.152739 45.876026,179.97045 46.8023103,180.409216 C47.7328342,180.826786 48.2627451,181.702199 47.9405593,182.340212 Z M54.2367892,187.958254 C53.6263318,188.524199 52.4329723,188.261363 51.6232682,187.366874 C50.7860088,186.474504 50.6291553,185.281144 51.2480912,184.70672 C51.8776254,184.140775 53.0349512,184.405731 53.8743302,185.298101 C54.7115892,186.201069 54.8748019,187.38595 54.2367892,187.958254 Z M58.5562413,195.146347 C57.7719732,195.691096 56.4895886,195.180261 55.6968417,194.042013 C54.9125733,192.903764 54.9125733,191.538713 55.713799,190.991845 C56.5086651,190.444977 57.7719732,190.936735 58.5753181,192.066505 C59.3574669,193.22383 59.3574669,194.58888 58.5562413,195.146347 Z M65.8613592,203.471174 C65.1597571,204.244846 63.6654083,204.03712 62.5716717,202.981538 C61.4524999,201.94927 61.1409122,200.484596 61.8446341,199.710926 C62.5547146,198.935137 64.0575422,199.15346 65.1597571,200.200564 C66.2704506,201.230712 66.6095936,202.705984 65.8613592,203.471174 Z M75.3025151,206.281542 C74.9930474,207.284134 73.553809,207.739857 72.1039724,207.313809 C70.6562556,206.875043 69.7087748,205.700761 70.0012857,204.687571 C70.302275,203.678621 71.7478721,203.20382 73.2083069,203.659543 C74.6539041,204.09619 75.6035048,205.261994 75.3025151,206.281542 Z M86.046947,207.473627 C86.0829806,208.529209 84.8535871,209.404622 83.3316829,209.4237 C81.8013,209.457614 80.563428,208.603398 80.5464708,207.564772 C80.5464708,206.498591 81.7483088,205.631657 83.2786917,205.606221 C84.8005962,205.576546 86.046947,206.424403 86.046947,207.473627 Z M96.6021471,207.069023 C96.7844366,208.099171 95.7267341,209.156872 94.215428,209.438785 C92.7295577,209.710099 91.3539086,209.074206 91.1652603,208.052538 C90.9808515,206.996955 92.0576306,205.939253 93.5413813,205.66582 C95.054807,205.402984 96.4092596,206.021919 96.6021471,207.069023 Z"
                fill="transparent"
                strokeWidth="10"
                whileHover={whileHover}
                variants={shouldReduceMotion ? variantsNoMotion : variants}
                custom={2}
              />
            </svg>
          </a>

          {/* info icon */}
          {/* <svg className={styles.infoIcon} viewBox="0 0 24 24">
            <motion.path
              d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm1 18h-2v-8h2v8zm-1-12.25c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25-1.25-.56-1.25-1.25.56-1.25 1.25-1.25z"
              fill="transparent"
              strokeWidth="1"
              whileHover={whileHover}
              variants={shouldReduceMotion ? variantsNoMotion : variants}
              custom={3}
            />
          </svg> */}
        </motion.nav>
      </div>
    </div>
  );
}

const MemoizedInfoCorner = memo(InfoCorner);

export { MemoizedInfoCorner as InfoCorner };
